import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, Loader2, Pencil, Save, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export type EvolutionRow = {
  id: string;
  session_at: string;
  notes: string;
  author_name: string;
};

interface PilatesEvolutionTimelineProps {
  items: EvolutionRow[];
  onEdit?: (id: string, sessionAtIso: string, notes: string) => Promise<boolean>;
  updatingId?: string | null;
  onDelete?: (id: string) => Promise<boolean>;
  deletingId?: string | null;
}

function toDatetimeLocalValue(isoDatetime: string): string {
  const dt = new Date(isoDatetime);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export function PilatesEvolutionTimeline({
  items,
  onEdit,
  updatingId = null,
  onDelete,
  deletingId = null,
}: PilatesEvolutionTimelineProps) {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [draftSessionAt, setDraftSessionAt] = useState("");

  const handleStartEdit = (row: EvolutionRow) => {
    setEditingRowId(row.id);
    setDraftNotes(row.notes);
    setDraftSessionAt(toDatetimeLocalValue(row.session_at));
  };

  const handleCancelEdit = () => {
    if (updatingId) return;
    setEditingRowId(null);
    setDraftNotes("");
    setDraftSessionAt("");
  };

  const handleSaveEdit = async () => {
    if (!editingRowId || !onEdit) return;
    const parsed = new Date(draftSessionAt);
    if (Number.isNaN(parsed.getTime())) return;
    const didSave = await onEdit(editingRowId, parsed.toISOString(), draftNotes);
    if (!didSave) return;
    setEditingRowId(null);
    setDraftNotes("");
    setDraftSessionAt("");
  };

  const handleDelete = async (row: EvolutionRow) => {
    if (!onDelete || updatingId === row.id || deletingId === row.id) return;
    const ok = window.confirm("Tem certeza que deseja excluir esta evolução? Esta ação não pode ser desfeita.");
    if (!ok) return;
    await onDelete(row.id);
  };

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-sky-100 bg-sky-50/40 dark:border-sky-900/40 dark:bg-sky-950/20 px-6 py-12 text-center text-sm text-muted-foreground">
        Nenhuma evolução registrada para este aluno ainda.
      </div>
    );
  }

  return (
    <div className="relative rounded-xl border border-sky-100/80 bg-white/80 dark:border-sky-900/30 dark:bg-slate-950/40 px-4 py-8 sm:px-8">
      <div
        className="pointer-events-none absolute left-1/2 top-8 bottom-8 w-px -translate-x-1/2 bg-sky-200 dark:bg-sky-800"
        aria-hidden
      />

      <h2 className="mb-8 text-center text-sm font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-400">
        Linha do tempo
      </h2>

      <ul className="relative space-y-10">
        {items.map((row, index) => {
          const dt = new Date(row.session_at);
          const metaOnLeft = index % 2 === 0;

          const metaBlock = (
            <div
              className={
                metaOnLeft
                  ? "flex flex-col items-end gap-1 pr-2 text-right text-sm sm:pr-4"
                  : "flex flex-col items-start gap-1 pl-2 text-left text-sm sm:pl-4"
              }
            >
              <time className="font-medium text-foreground tabular-nums">
                {format(dt, "dd/MM/yyyy HH:mm", { locale: ptBR })}
              </time>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Por: {row.author_name}
              </p>
            </div>
          );

          const node = (
            <div className="relative z-10 flex justify-center">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-sky-200 bg-sky-100 text-sky-600 shadow-sm dark:border-sky-700 dark:bg-sky-950 dark:text-sky-300">
                <Calendar className="h-4 w-4" aria-hidden />
              </div>
            </div>
          );

          const notesBlock = (
            <div
              className={
                metaOnLeft
                  ? "min-w-0 pl-2 sm:pl-4"
                  : "flex min-w-0 justify-end pr-2 sm:pr-4"
              }
            >
              {editingRowId === row.id ? (
                <div className="inline-block w-full max-w-full rounded-lg border border-sky-200 bg-sky-50/90 px-4 py-3 text-left text-sm leading-relaxed text-foreground shadow-sm dark:border-sky-800 dark:bg-sky-950/50">
                  <div className="space-y-3">
                    <Input
                      type="datetime-local"
                      value={draftSessionAt}
                      onChange={(e) => setDraftSessionAt(e.target.value)}
                      disabled={updatingId === row.id}
                    />
                    <Textarea
                      className="min-h-[120px] resize-y"
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.target.value)}
                      disabled={updatingId === row.id}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        disabled={updatingId === row.id}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-sky-600 hover:bg-sky-700 dark:bg-sky-600 dark:hover:bg-sky-500"
                        onClick={() => void handleSaveEdit()}
                        disabled={updatingId === row.id || !draftSessionAt.trim() || !draftNotes.trim()}
                      >
                        {updatingId === row.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Salvando…
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Salvar edição
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="inline-block max-w-full rounded-lg border border-sky-100 bg-sky-50/90 px-4 py-3 text-left text-sm leading-relaxed text-foreground shadow-sm dark:border-sky-900/50 dark:bg-sky-950/50">
                  <div className="mb-2 flex justify-end">
                    {onEdit ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 px-0 text-muted-foreground hover:text-foreground"
                        onClick={() => handleStartEdit(row)}
                        disabled={deletingId === row.id}
                        aria-label="Editar evolução"
                        title="Editar evolução"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                    {onDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-7 w-7 px-0 text-destructive hover:text-destructive"
                        onClick={() => void handleDelete(row)}
                        disabled={deletingId === row.id || updatingId === row.id}
                        aria-label="Excluir evolução"
                        title="Excluir evolução"
                      >
                        {deletingId === row.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    ) : null}
                  </div>
                  <p className="whitespace-pre-wrap break-words">{row.notes}</p>
                </div>
              )}
            </div>
          );

          return (
            <li key={row.id}>
              <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 sm:gap-3">
                {metaOnLeft ? (
                  <>
                    {metaBlock}
                    {node}
                    {notesBlock}
                  </>
                ) : (
                  <>
                    {notesBlock}
                    {node}
                    {metaBlock}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
